import React, { FC } from 'react';
import styled from 'styled-components';
import {
  OvulatoryEventLoadStatus,
  OvulatoryEventTableDay,
} from '../../../common/model/type/ovulatory-event-prediction.type';
import {
  HORMONE_SERIES_ORDER,
  HORMONE_TRENDS_SERIES,
} from '../../../common/utils/services/hormone-trends-utils';
import { OVULATORY_EVENT_PAGE_SIZE } from '../../../common/utils/services/ovulatory-event-prediction-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvOvulatoryEventDayTableRow from '../molecules/OvOvulatoryEventDayTableRow';
import OvPaginationBar from '../molecules/OvPaginationBar';
import {
  OVULATORY_EVENT_APPROXIMATE_RULE_DESCRIPTION,
  OVULATORY_EVENT_APPROXIMATE_RULE_LABEL,
} from './ovulatory-event-prediction-presentation';

interface OvOvulatoryEventDayTableProps {
  days: OvulatoryEventTableDay[];
  detailsStatus: OvulatoryEventLoadStatus;
  scansStatus: OvulatoryEventLoadStatus;
  total: number;
  offset: number;
  onPageChange: (page: number) => void;
}

const MODEL_COLUMNS = ['Prediction', 'Score / threshold', 'Evidence'];
const OBSERVED_COLUMNS = ['Outcome', 'Review'];
const COLUMN_COUNT =
  2 +
  HORMONE_SERIES_ORDER.length +
  MODEL_COLUMNS.length +
  1 +
  OBSERVED_COLUMNS.length;

const OvOvulatoryEventDayTable: FC<OvOvulatoryEventDayTableProps> = ({
  days,
  detailsStatus,
  scansStatus,
  total,
  offset,
  onPageChange,
}) => (
  <section aria-labelledby="ovulatory-event-day-table-title">
    <Header>
      <Title id="ovulatory-event-day-table-title">Days</Title>
      <Meta>
        {offset + 1} to {offset + days.length} of {total} scored days · Newest
        first
      </Meta>
      <Hint>Expand a day for provenance and model inputs</Hint>
    </Header>
    <Scroll>
      <Table>
        <thead>
          <GroupRow>
            <td colSpan={2} />
            <th scope="colgroup" colSpan={HORMONE_SERIES_ORDER.length}>
              Scan results
            </th>
            <th scope="colgroup" colSpan={MODEL_COLUMNS.length}>
              Model 1A
            </th>
            <th scope="colgroup">Current logic</th>
            <th scope="colgroup" colSpan={OBSERVED_COLUMNS.length}>
              Observed afterwards
            </th>
          </GroupRow>
          <tr>
            <th>
              <VisuallyHidden>Details</VisuallyHidden>
            </th>
            <th scope="col">Day</th>
            {HORMONE_SERIES_ORDER.map((seriesId) => (
              <NumberHeader key={seriesId} scope="col">
                {HORMONE_TRENDS_SERIES[seriesId].displayName}
                <Unit>{HORMONE_TRENDS_SERIES[seriesId].unit}</Unit>
              </NumberHeader>
            ))}
            {MODEL_COLUMNS.map((label) => (
              <th key={label} scope="col">
                {label}
              </th>
            ))}
            <th scope="col">Rule status</th>
            {OBSERVED_COLUMNS.map((label) => (
              <th key={label} scope="col">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <OvOvulatoryEventDayTableRow
              key={day.row.key}
              day={day}
              detailsStatus={detailsStatus}
              scansStatus={scansStatus}
              columnCount={COLUMN_COUNT}
            />
          ))}
        </tbody>
      </Table>
    </Scroll>
    <Footer>
      <OvPaginationBar
        total={total}
        limit={OVULATORY_EVENT_PAGE_SIZE}
        offset={offset}
        onChange={(_event, page) => onPageChange(page)}
      />
    </Footer>
    <Note title={OVULATORY_EVENT_APPROXIMATE_RULE_DESCRIPTION}>
      Approaching: the model's positive state, approaching ovulatory event.{' '}
      {OVULATORY_EVENT_APPROXIMATE_RULE_LABEL}: scored after the day, with the
      member's data as it was then. A dash in a scan column means no reading of
      that hormone on the day.
    </Note>
  </section>
);

export default OvOvulatoryEventDayTable;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 2rem;
  margin-bottom: 0.75rem;
`;

const Title = styled.h3`
  margin: 0;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.LARGE};
  font-weight: bold;
`;

const Meta = styled.span`
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;

const Hint = styled(Meta)`
  margin-left: auto;
`;

const Scroll = styled.div`
  position: relative;
  overflow-x: auto;
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};

  th,
  td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid ${Colours.LIGHT_GRAY_BLUE};
    vertical-align: middle;
  }

  th:first-child,
  td:first-child {
    width: 1.5rem;
    padding-right: 0;
  }

  thead th {
    border-bottom-color: ${Colours.OV_BORDER_COLOR_V3};
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
    font-weight: normal;
    text-align: left;
    white-space: nowrap;
  }
`;

const GroupRow = styled.tr`
  && th,
  && td {
    padding-bottom: 0.125rem;
    border-bottom: 0;
    color: ${Colours.OV_BASE};
    font-weight: bold;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;

const NumberHeader = styled.th`
  && {
    text-align: right;
  }
`;

const Unit = styled.span`
  display: block;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const Note = styled.p`
  margin: 0.75rem 0 0;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;
